import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Tag } from "@blueprintjs/core";
import { Row } from "jsxstyle";
import useToast from "hooks/useToast";
import useAsync from "hooks/useAsync";
import Table from "components/Table";
import Button from "components/Button";
import Separator from "components/Separator";
import CurrencyFormat from "components/CurrencyFormat";
import loadEntityMasterRoles from "services/entityMaster/loadEntityMasterRoles";
import moment from "moment";

function EntityMasterABSRelationshipsTable({ entityId }) {
  const { showError } = useToast();

  const [fetch, pending, data] = useAsync({
    promiseFn: loadEntityMasterRoles,
    onError: () => showError("Error loading the ABSRelationships"),
  });

  React.useEffect(() => {
    fetch({
      id: entityId,
      type: "abs",
      params: {
        page: 1,
        size: 20,
      },
    });
  }, [fetch, entityId]);

  return (
    <Table
      pending={pending}
      data={data && data.data}
      rowKey="deal_code"
      scroll={{ x: 2780 }}
      columns={[
        {
          title: "Roles",
          dataIndex: "roles",
          key: "roles",
          fixed: "left",
          render: (roles) => (
            <Row>
              {roles.map((role) => (
                <React.Fragment key={role}>
                  <Tag intent="primary" key={role}>
                    {role}
                  </Tag>
                  <Separator size="xs" />
                </React.Fragment>
              ))}
            </Row>
          ),
        },
        {
          title: "Deal Code",
          dataIndex: "deal_code",
          key: "deal_code",
          width: 120,
        },
        {
          title: "Offering Type",
          dataIndex: "offering_type",
          key: "offering_type",
          width: 180,
        },
        {
          title: "Deal Type",
          dataIndex: "deal_type",
          key: "deal_type",
          width: 140,
        },
        {
          title: "Pricing Date",
          dataIndex: "pricing_date",
          key: "pricing_date",
          width: 120,
          render: (date) => (date ? moment(date).format("MM/DD/YYYY") : null),
        },
        {
          title: "Closing Date",
          dataIndex: "closing_date",
          key: "closing_date",
          width: 120,
          render: (date) => (date ? moment(date).format("MM/DD/YYYY") : null),
        },
        {
          title: "Abbrv",
          dataIndex: "abbrv",
          key: "abbrv",
          width: 120,
        },
        {
          title: "Issuer",
          dataIndex: "issuer",
          key: "issuer",
          width: 120,
        },
        {
          title: "Series",
          dataIndex: "series",
          key: "series",
          width: 120,
        },
        {
          title: "Deal Amount",
          dataIndex: "deal_amount",
          key: "deal_amount",
          width: 120,
          render: (amount) => <CurrencyFormat amount={amount || 0} />,
        },
        {
          title: "Retained Amount",
          dataIndex: "retained_amount",
          key: "retained_amount",
          width: 120,
          render: (amount) => <CurrencyFormat amount={amount || 0} />,
        },
        {
          title: "Upsized",
          dataIndex: "upsized",
          key: "upsized",
          width: 120,
          render: (upsized) => (upsized ? "Yes" : "No"),
        },
        {
          title: "Seller",
          dataIndex: "seller",
          key: "seller",
          width: 120,
        },
        {
          title: "Sponsor",
          dataIndex: "sponsor",
          key: "sponsor",
          width: 120,
        },
        {
          title: "Master Servicer",
          dataIndex: "master_servicer",
          key: "master_servicer",
          width: 120,
        },
        {
          title: "Primary Servicer",
          dataIndex: "primary_servicer",
          key: "primary_servicer",
          width: 120,
        },
        {
          title: "Special Servicer",
          dataIndex: "special_servicer",
          key: "special_servicer",
          width: 120,
        },
        {
          title: "Enhancement",
          dataIndex: "enhancement",
          key: "enhancement",
          width: 120,
        },
        {
          title: "Trustee",
          dataIndex: "trustee",
          key: "trustee",
          width: 120,
        },
        {
          title: "Counsel Uw",
          dataIndex: "counsel_uw",
          key: "counsel_uw",
          width: 120,
        },
        {
          title: "Denomination",
          dataIndex: "denomination",
          key: "denomination",
          width: 120,
        },
        {
          title: "Denomination Retained",
          dataIndex: "denomination_retained",
          key: "denomination_retained",
          width: 120,
        },
        {
          title: "Actions",
          dataIndex: "",
          key: "",
          width: 100,
          fixed: "right",
          render: (item) => (
            <Link to={`/abs/edit/${item.deal_code}`} target="_blank">
              <Button size="sm" icon="document-open" onClick={null} />
            </Link>
          ),
        },
      ]}
    />
  );
}

EntityMasterABSRelationshipsTable.propTypes = {
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default EntityMasterABSRelationshipsTable;
